"use client";

import { useEffect, useRef, useState } from "react";

export type SummitNavItem = {
  id: string;
  label: string;
  children: { id: string; label: string }[];
};

/**
 * Scrollspy jump nav for a summit day page.
 * Desktop (xl+): fixed rail in the left gutter. Below xl: sticky horizontal bar
 * under the floating navbar. Targets need matching ids + scroll-mt classes.
 */
export default function SummitDayNav({
  items,
  heading,
}: {
  items: SummitNavItem[];
  heading: string;
}) {
  const flat = items.flatMap((item) => [
    { id: item.id, label: item.label, sub: false },
    ...item.children.map((c) => ({ id: c.id, label: c.label, sub: true })),
  ]);

  const [activeId, setActiveId] = useState(flat[0]?.id ?? "");
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ids = flat.map((f) => f.id);

    // Active section = the last target whose top has passed the offset line.
    const update = () => {
      const offset = 160;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= offset) {
          current = id;
        }
      }
      setActiveId(current);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Keep the active chip visible in the mobile bar.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const link = bar.querySelector<HTMLElement>(`[data-id="${CSS.escape(activeId)}"]`);
    if (link) {
      const barRect = bar.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      if (linkRect.left < barRect.left || linkRect.right > barRect.right) {
        bar.scrollTo({
          left: link.offsetLeft - 24,
          behavior: "smooth",
        });
      }
    }
  }, [activeId]);

  // Plain anchor navigation: html has `scroll-smooth`, targets carry
  // scroll-mt offsets, so the browser handles the smooth jump natively.

  const activeClasses = "text-brand-red dark:text-brand-gold";

  return (
    <>
      {/* Desktop rail */}
      <nav
        aria-label={heading}
        className="fixed left-5 top-44 z-40 hidden w-52 xl:block"
      >
        <p className="font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400 dark:text-zinc-500">
          {heading}
        </p>
        <ul className="mt-4 space-y-3">
          {items.map((item, index) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                aria-current={activeId === item.id ? "true" : undefined}
                className={`flex gap-2 text-[13px] font-medium leading-snug transition-colors ${
                  activeId === item.id
                    ? activeClasses
                    : "text-zinc-600 hover:text-black dark:text-zinc-400 dark:hover:text-white"
                }`}
              >
                <span className="font-display shrink-0 text-xs font-semibold">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="line-clamp-2">{item.label}</span>
              </a>
              {item.children.length > 0 && (
                <ul className="mt-2 space-y-1.5 pl-6">
                  {item.children.map((child) => (
                    <li key={child.id}>
                      <a
                        href={`#${child.id}`}
                        aria-current={activeId === child.id ? "true" : undefined}
                        className={`block text-xs leading-snug transition-colors ${
                          activeId === child.id
                            ? activeClasses
                            : "text-zinc-500 hover:text-black dark:text-zinc-500 dark:hover:text-white"
                        }`}
                      >
                        {child.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Mobile / tablet sticky bar */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-md dark:bg-zinc-950/90 xl:hidden">
        <div
          ref={barRef}
          style={{ scrollbarWidth: "none" }}
          className="flex items-center gap-5 overflow-x-auto px-6 pb-3 pt-20 md:pt-28"
        >
          {items.map((item, index) => (
            <div key={item.id} className="flex items-center gap-5">
              <a
                href={`#${item.id}`}
                data-id={item.id}
                aria-current={activeId === item.id ? "true" : undefined}
                className={`font-display whitespace-nowrap text-xs font-semibold uppercase tracking-wide transition-colors ${
                  activeId === item.id
                    ? activeClasses
                    : "text-zinc-700 dark:text-zinc-300"
                }`}
              >
                {index + 1}. {shorten(item.label)}
              </a>
              {item.children.map((child) => (
                <a
                  key={child.id}
                  href={`#${child.id}`}
                  data-id={child.id}
                  aria-current={activeId === child.id ? "true" : undefined}
                  className={`whitespace-nowrap text-xs transition-colors ${
                    activeId === child.id
                      ? `font-medium ${activeClasses}`
                      : "text-zinc-500 dark:text-zinc-400"
                  }`}
                >
                  {child.label}
                </a>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/** First few words of a long message title so the mobile bar stays scannable. */
function shorten(title: string): string {
  const words = title.split(" ");
  return words.length <= 4 ? title : `${words.slice(0, 4).join(" ")}…`;
}
